/** Shopify adapter: mount widgets, resolve calendars, render text and handle section lifecycle. */
(() => {
  'use strict';
  if (window.DeliveryEstimate) return;
  const instances = new Map();
  const lifecycle = new AbortController();
  class DeliveryEstimate {
    constructor(root) {
      this.root = root; this.version = 0;
      this.config = JSON.parse(root.querySelector('[data-delivery-config]').textContent);
      this.rangeTemplate = root.querySelector('.delivery-estimate__text').cloneNode(true);
      this.update();
    }
    async update() {
      const version = ++this.version;
      this.root.hidden = true;
      const { profile, rules, country, enabled } = this.config;
      // Ambiguous or missing rules never produce a guessed delivery date.
      if (!enabled) return;
      try {
        const now = new Date();
        const status = this.config.status == null ? null : window.FizzyDeliveryStatus.resolve(this.config.status, now, profile.timezone, country);
        const text = this.root.querySelector('.delivery-estimate__text');
        if (status?.mode === 'pausiert') {
          text.textContent = status.notice || this.config.pausedText;
          this.root.hidden = false;
          return;
        }
        const rule = window.FizzyDeliveryStatus.countryRule(profile, rules, country, Boolean(status) && status.calculationMode !== 'profil');
        if (!rule) return;
        let result;
        if (status) result = window.FizzyDeliveryStatus.calculate(profile, rule, now, status);
        else {
          const year = Number(window.DeliveryEngine.clock(now, profile.timezone).date.getUTCFullYear());
          // 180 working days may cross two year boundaries when calendars are sparse.
          const years = [year, year + 1, year + 2];
          const [production, delivery] = await Promise.all([
            window.DeliveryCalendar.load(profile.country, profile.region, years),
            window.DeliveryCalendar.load(rule.country, rule.region, years)
          ]);
          if (version !== this.version || !this.root.isConnected) return;
          result = window.DeliveryEngine.calculate(profile, rule, new Date(), production.dates, delivery.dates);
        }
        if (version !== this.version || !this.root.isConnected) return;
        const locale = new Intl.Locale(this.config.locale, { region: this.config.localeCountry });
        const formatter = new Intl.DateTimeFormat(locale.toString(), { day: 'numeric', month: 'short', timeZone: 'UTC' });
        const first = window.DeliveryCalendar.date(result.min), last = window.DeliveryCalendar.date(result.max);
        const single = result.min === result.max;
        // Keep both endpoints explicit; the delivery calculation remains unchanged.
        const range = single ? formatter.format(first) : `${formatter.format(first)} – ${formatter.format(last)}`;
        const singleTemplate = this.root.querySelector('[data-delivery-single]');
        const content = single && singleTemplate ? singleTemplate.content.cloneNode(true) : this.rangeTemplate.cloneNode(true);
        const slot = content.querySelector('[data-delivery-range]');
        if (!slot) throw new Error('Missing delivery text slot');
        slot.textContent = range;
        if (content.nodeType === 11) text.replaceChildren(content);
        else text.replaceChildren(...content.childNodes);
        if (status && result.notice) {
          const notice = document.createElement('span');
          notice.className = 'delivery-estimate__notice';
          notice.textContent = result.notice;
          text.append(notice);
        }
        this.root.dataset.calendarSource = 'local';
        this.root.hidden = false;
      } catch (error) {
        this.root.hidden = true;
        if (window.Shopify?.designMode) console.warn('Delivery estimate:', error.message);
      }
    }
    destroy() { this.version++; }
  }
  function mount(scope = document) {
    const roots = [...scope.querySelectorAll('[data-delivery-estimate]')];
    if (scope.matches?.('[data-delivery-estimate]')) roots.push(scope);
    roots.forEach(root => {
      if (!instances.has(root)) {
        try { instances.set(root, new DeliveryEstimate(root)); }
        catch (error) { if (window.Shopify?.designMode) console.warn('Delivery configuration:', error.message); }
      }
    });
  }
  const options = { signal: lifecycle.signal };
  document.addEventListener('shopify:section:load', event => mount(event.target), options);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (root === event.target || event.target.contains(root)) { instance.destroy(); instances.delete(root); }
    });
  }, options);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) instances.forEach(instance => instance.update());
  }, options);
  const timer = setInterval(() => instances.forEach(instance => instance.update()), 60000);
  mount();
  window.DeliveryEstimate = { destroy() {
    clearInterval(timer); lifecycle.abort(); instances.forEach(instance => instance.destroy()); instances.clear();
    delete window.DeliveryEstimate;
  } };
})();
